import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren
} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidatorFn,
    Validators
} from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ICardSelect } from '../../interfaces/ICardSelect.interface';
import { ICheckForCardRequest, RequestMode } from '../../interfaces/ICheckForCardRequest.interface';
import { DirectDebitService } from '../../services/direct-debit.service';
import { Router, ActivatedRoute } from '@angular/router';
import { AppConfigsService } from '../../../../config/app-configs.service';
import { Subscription } from 'rxjs';
import { ErrorPopupModal } from '../error-popup/constant.modal';
import { CalculatorPopupComponent } from './calculator-popup/calculator-popup.component';
import { CalculatorModal } from './calculator-popup/constant.modal';
import { GtmService } from '../../../../shared/cardholders-core/services/gtm.service';
import { IDirectDebitBenefitDataRequest } from '../../interfaces/IDirectDebitBenefitDataRequest.interface';
import { ModalService } from '../../../../shared/modules/bs-modal/services/modal.service';

function CardValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
        if (typeof control.value === 'string') {
            if (control.value.length === 0) {
                return { required: true };
            }
            if (control.value.includes('xxxx-xxxx-xxxx-')) {
                return null;
            }
            if (control.value.length < 7) {
                return { length: 7 };
            }
            if (control.value.length > 16) {
                return { length: 16 };
            }
            return {};
        }
        return { required: true };
    };
}

@Component({
    selector: 'app-card-autocomplete',
    templateUrl: './card-autocomplete.component.html',
    styleUrls: ['./card-autocomplete.component.scss']
})
export class CardAutocompleteComponent implements OnInit, OnDestroy {
    @Input() userCards: ICardSelect[];
    @ViewChildren(MatAutocompleteTrigger) autocomplete: QueryList<MatAutocompleteTrigger>;
    @ViewChildren('inputAuto') inputAuto: QueryList<ElementRef>;
    @Output() select = new EventEmitter<ICheckForCardRequest>();
    @Input() displayBenefit: boolean;
    subs: Subscription[] = [];
    form: FormGroup;
    usedCard: ICardSelect[] = [];
    ISHokMultipleCard = false;
    get cardsAsFormArray(): AbstractControl[] {
        const cards = this.form.get('cards') as FormArray;
        return cards.controls;
    }
    constructor(
        private fb: FormBuilder,
        private directDebSrv: DirectDebitService,
        private router: Router,
        private modalSrv: ModalService,
        public config: AppConfigsService,
        private activeRoute: ActivatedRoute,
        private gtmService: GtmService
    ) {}
    ngOnDestroy(): void {
        this.subs.forEach((s) => s.unsubscribe());
    }
    ngOnInit(): void {
        this.ISHokMultipleCard = this.config.appConfigs.FLAG_ISHokMultipleCard;
        this.form = this.fb.group({ cards: this.fb.array([]) });
        this.addCardForm();
    }
    errorPopup(): void {
        this.subs.push(
            this.modalSrv.openModal(ErrorPopupModal).subscribe((x) => {
                if (x) {
                    const cards = this.form.get('cards') as FormArray;
                    cards.enable();
                    cards.reset();
                    this.usedCard = [];
                } else {
                    this.router.navigate(['/']);
                }
            })
        );
    }
    markFormGroupTouched(formGroup: FormGroup): void {
        (<any>Object).values(formGroup.controls).forEach((control) => {
            if (control.controls) {
                // control is a FormGroup
                this.markFormGroupTouched(control);
            } else {
                // control is a FormControl
                control.markAsTouched();
                control.markAsDirty();
                control.updateValueAndValidity();
            }
        });
    }
    removeCardForm(index: number): void {
        const removeAdd = this.form.get('cards') as FormArray;
        const cards = this.form.get('cards') as FormArray;
        const cardIndex: string = cards.controls[index].get('cardIndex').value;
        if (this.usedCard.find((x) => x.cardIndex === cardIndex)) {
            this.usedCard = this.usedCard.filter((x) => x.cardIndex !== cardIndex);
        }
        removeAdd.removeAt(index);
    }
    addCardForm(): void {
        const add = this.form.get('cards') as FormArray;

        const toAdd = this.fb.group({
            cardNumber: new FormControl('', [CardValidator()]),
            type: new FormControl(''),
            cardIndex: new FormControl(''),
            error: new FormControl('', [Validators.maxLength(1)]),
            checked: new FormControl(false)
        });

        add.push(toAdd);
    }

    resetAutoComplete(event: any, index: number): void {
        const cards = this.form.get('cards') as FormArray;
        const cardIndex: string = cards.controls[index].get('cardIndex').value;
        if (this.usedCard.find((x) => x.cardIndex === cardIndex)) {
            this.usedCard = this.usedCard.filter((x) => x.cardIndex !== cardIndex);
        }
        cards.controls[index].get('cardNumber').enable();
        cards.controls[index].reset();
        setTimeout(() => {
            this.autocomplete.toArray()[index].openPanel();
        }, 100);
    }

    onSelect(event: MatAutocompleteSelectedEvent, index: number): void {
        this.inputAuto.forEach((ref) => ref.nativeElement.blur());
        this.usedCard.push(event.option.value);
        const cards = (this.form.get('cards') as FormArray).controls[index] as FormGroup;
        cards.controls.cardNumber.setValue('xxxx-xxxx-xxxx-' + event.option.value.shortCardNumber);
        cards.controls.cardNumber.disable();
        cards.controls.type.setValue(RequestMode.Index);
        cards.controls.cardIndex.setValue(event.option.value.cardIndex);
    }

    valueChange(changeEvent: any, index: number): void {
        setTimeout(() => {
            const cards = (this.form.get('cards') as FormArray).controls[index] as FormGroup;
            cards.controls.error.reset();
            if (cards.controls.type.value === RequestMode.Index) {
                return;
            } else {
                cards.controls.type.setValue(RequestMode.FullCard);
            }
        }, 100);
    }
    get filtred(): ICardSelect[] {
        return this.userCards.filter((x) => !this.usedCard.find((b) => x.cardIndex === b.cardIndex));
    }
    getControl(index: number, name: string): FormControl | AbstractControl {
        const cards = (this.form.get('cards') as FormArray).controls[index] as FormGroup;
        return cards.get(name);
    }

    openCalc(): void {
        this.gtmService.pushDirectDebit('phase1', 'calculator');
        CalculatorModal.options.initialState = {} as Object;
        this.subs.push(
            this.modalSrv.openModal(CalculatorModal).subscribe((x) => {
                if (x) {
                    this.goToCalcResults(x);
                }
            })
        );
    }
    goToCalcResults(directDebitsSum: number): void {
        const req: IDirectDebitBenefitDataRequest = {
            directDebitsSum: directDebitsSum
        };
        this.directDebSrv.getBenefitsData(req).subscribe((res) => {
            switch (res.returnCode) {
                case 0:
                    this.directDebSrv.next = 'calculator-result';
                    this.router.navigate(['../calculator-result'], { relativeTo: this.activeRoute });
                    break;
                default:
                    this.directDebSrv.next = 'error';
                    this.router.navigate(['../error'], { relativeTo: this.activeRoute });
                    break;
            }
        });
    }

    submit(): void {
        const cards = this.form.get('cards') as FormArray;
        this.autocomplete.toArray().forEach((auto) => auto.closePanel());
        for (const card of cards.controls) {
            const c = card as FormGroup;
            this.markFormGroupTouched(c);
        }
        if (this.form.valid) {
            const form = this.form.getRawValue();
            const req: ICheckForCardRequest[] = form.cards.map((element, index) => {
                return {
                    requestMode: element.type,
                    cardIndex: element.cardIndex,
                    cardNumber: element.cardNumber,
                    iInL: index
                };
            });
            this.gtmService.pushDirectDebit('phase1', 'card num entered');
            this.gtmService.pushDirectDebitWorkFlow('phase1', 'continue', '1');
            this.directDebSrv.checkForCard(req).subscribe((res) => {
                switch (res.returnCode) {
                    case 0:
                        this.directDebSrv.next = 'destination-card';
                        this.router.navigate(['../destination-card'], { relativeTo: this.activeRoute });
                        break;
                    case 10:
                        this.gtmService.pushDirectDebit('phase1', 'error card num');
                        res.result.checkForCards.forEach((card) => {
                            const cardsRef = (this.form.get('cards') as FormArray).controls[card.iInL] as FormGroup;
                            if (card.cardRC !== 0) {
                                switch (card.cardRC) {
                                    case 10:
                                        cardsRef.controls.error.setValue(
                                            'כבר קיימת בקשה על הכרטיס ממנו ביקשת להעביר הוראת קבע. אפשר להתעדכן בפירוט החיובים החודשי הקרוב שלך. '
                                        );
                                        break;
                                    case 11:
                                        cardsRef.controls.error.setValue('אפשר להעביר הוראות קבע רק מכרטיסים שלך');
                                        break;
                                    case 12:
                                        cardsRef.controls.error.setValue(
                                            'אופס, לא מצאנו הוראות קבע שאפשר להעביר מהכרטיס הזה'
                                        );
                                        break;
                                    case 9:
                                        this.errorPopup();
                                        break;

                                    default:
                                        break;
                                }
                            } else {
                                cardsRef.controls.checked.setValue(true);
                            }
                        });
                        break;
                    case 11:
                        this.gtmService.pushDirectDebit('phase1', 'error card num');
                        this.directDebSrv.rcError = 13;
                        this.directDebSrv.fromError = 'Destination';
                        this.directDebSrv.next = 'error';
                        this.router.navigate(['../error'], { relativeTo: this.activeRoute });
                        break;
                    case 9:
                    default:
                        this.gtmService.pushDirectDebit('phase1', 'error card num');
                        this.errorPopup();
                        break;

                }
            });
        } else {
            this.gtmService.pushDirectDebit('phase1', 'error card num');
        }
    }
}
